import type { PickList, PickItem, User } from '../types';

export interface ERPConfig {
  type: 'sap' | 'oracle' | 'dynamics' | 'netsuite' | 'fishbowl' | 'cin7' | 'custom';
  endpoint: string;
  apiKey: string;
  username?: string;
  password?: string;
  clientId?: string;
  clientSecret?: string;
  syncInterval: number;
  fieldMappings: { [key: string]: string };
  enabled: boolean;
  lastSync?: string;
}

export interface ERPSyncResult {
  success: boolean;
  recordsProcessed: number;
  errors: string[];
  lastSyncTime: string;
}

export class ERPIntegrationService {
  private config: ERPConfig;

  constructor(config: ERPConfig) {
    this.config = config;
  }

  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${this.config.endpoint}/health`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      return response.ok;
    } catch (error) {
      console.error('ERP connection test failed:', error);
      return false;
    }
  }

  async syncOrders(): Promise<ERPSyncResult> {
    try {
      const response = await fetch(`${this.config.endpoint}/orders`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const orders = await response.json();
      const transformedOrders = this.transformOrders(orders);
      
      // Process orders in batches
      const batchSize = 100;
      let processed = 0;
      const errors: string[] = [];

      for (let i = 0; i < transformedOrders.length; i += batchSize) {
        const batch = transformedOrders.slice(i, i + batchSize);
        try {
          await this.processBatch(batch);
          processed += batch.length;
        } catch (error) {
          errors.push(`Batch ${i / batchSize + 1}: ${error}`);
        }
      }

      return {
        success: errors.length === 0,
        recordsProcessed: processed,
        errors,
        lastSyncTime: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        recordsProcessed: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        lastSyncTime: new Date().toISOString()
      };
    }
  }

  async syncInventory(): Promise<ERPSyncResult> {
    try {
      const response = await fetch(`${this.config.endpoint}/inventory`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const inventory = await response.json();
      const transformedInventory = this.transformInventory(inventory);
      
      // Update local inventory
      await this.updateLocalInventory(transformedInventory);

      return {
        success: true,
        recordsProcessed: transformedInventory.length,
        errors: [],
        lastSyncTime: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        recordsProcessed: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        lastSyncTime: new Date().toISOString()
      };
    }
  }

  async updateOrderStatus(orderId: string, status: string, items: PickItem[]): Promise<boolean> {
    try {
      const payload = this.transformOrderUpdate(orderId, status, items);
      
      const response = await fetch(`${this.config.endpoint}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to update order status:', error);
      return false;
    }
  }

  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    switch (this.config.type) {
      case 'sap':
        headers['Authorization'] = `Basic ${btoa(`${this.config.username}:${this.config.password}`)}`;
        break;
      case 'oracle':
      case 'netsuite':
        headers['Authorization'] = `Bearer ${this.config.apiKey}`;
        break;
      case 'dynamics':
        headers['Authorization'] = `Bearer ${this.config.apiKey}`;
        headers['OData-MaxVersion'] = '4.0';
        break;
      default:
        headers['X-API-Key'] = this.config.apiKey;
    }

    return headers;
  }

  private transformOrders(orders: any[]): PickList[] {
    return orders.map(order => ({
      id: this.mapField(order, 'id'),
      orderNumber: this.mapField(order, 'orderNumber'),
      status: 'pending' as const,
      priority: this.mapField(order, 'priority') || 'medium',
      customerName: this.mapField(order, 'customerName'),
      totalItems: parseInt(this.mapField(order, 'totalItems')) || 0,
      pickedItems: 0,
      estimatedTime: parseInt(this.mapField(order, 'estimatedTime')) || 30,
      createdAt: this.mapField(order, 'createdAt') || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      dueDate: this.mapField(order, 'dueDate'),
      pickingType: 'individual' as const,
      temperatureZone: this.mapField(order, 'temperatureZone') as any
    }));
  }

  private transformInventory(inventory: any[]): any[] {
    return inventory.map(item => ({
      sku: this.mapField(item, 'sku'),
      name: this.mapField(item, 'name'),
      quantity: parseInt(this.mapField(item, 'quantity')) || 0,
      location: this.mapField(item, 'location'),
      expiryDate: this.mapField(item, 'expiryDate'),
      lotNumber: this.mapField(item, 'lotNumber'),
      temperatureZone: this.mapField(item, 'temperatureZone')
    }));
  }

  private transformOrderUpdate(orderId: string, status: string, items: PickItem[]): any {
    return {
      orderId,
      status,
      items: items.map(item => ({
        [this.config.fieldMappings['item_sku'] || 'sku']: item.productSku,
        [this.config.fieldMappings['quantity_picked'] || 'quantityPicked']: item.quantityPicked,
        [this.config.fieldMappings['status'] || 'status']: item.status
      })),
      updatedAt: new Date().toISOString()
    };
  }

  private mapField(source: any, targetField: string): any {
    const mapping = this.config.fieldMappings[targetField];
    return mapping ? source[mapping] : source[targetField];
  }

  private async processBatch(orders: PickList[]): Promise<void> {
    // Implementation for processing order batches
    // This would integrate with your local database/store
    console.log(`Processing batch of ${orders.length} orders`);
  }

  private async updateLocalInventory(inventory: any[]): Promise<void> {
    // Implementation for updating local inventory
    console.log(`Updating ${inventory.length} inventory items`);
  }
}

// ERP-specific implementations
export class SAPIntegration extends ERPIntegrationService {
  async syncMasterData(): Promise<ERPSyncResult> {
    // SAP-specific master data sync
    return super.syncInventory();
  }
}

export class OracleIntegration extends ERPIntegrationService {
  async syncWMSData(): Promise<ERPSyncResult> {
    // Oracle WMS-specific data sync
    return super.syncInventory();
  }
}

export class DynamicsIntegration extends ERPIntegrationService {
  async syncBusinessCentral(): Promise<ERPSyncResult> {
    // Dynamics 365 Business Central sync
    return super.syncInventory();
  }
}